import { FormHelperText, Stack, Box, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import {
  ApiRequest_UpdatePassword_User,
  useUpdatePassword,
} from '@/api/endpoints/user/useUpdatePassword';

import FormPassword from '@/components/form/FormPassword';

type FormData = ApiRequest_UpdatePassword_User & {
  confirm_password: string;
};

const PasswordForm: React.FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
  } = useForm<FormData>();

  const [formError, setFormError] = useState<Error | null>(null);

  const updatePasswordMutation = useUpdatePassword({
    onError: error => {
      setFormError(error);
    },
  });

  const onSubmit = useCallback(
    (data: FormData) => {
      setFormError(null);
      return updatePasswordMutation.mutateAsync(data);
    },
    [updatePasswordMutation]
  );

  return (
    <Stack spacing={1} component="form" onSubmit={handleSubmit(onSubmit)}>
      {/* New Password Field */}
      <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: 300 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5 }}
        >
          New Password
        </Typography>
        <FormPassword
          size="small"
          placeholder="Enter your new password"
          error={!!errors.password}
          helperText={errors.password?.message?.toString()}
          control={control}
          required
          sx={{
            '& .MuiInputBase-root': {
              height: 40,
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '0 8px',
              display: 'flex',
              alignItems: 'center',
              boxSizing: 'border-box',
            },
          }}
          {...register('password', {
            required: 'New Password is required',
            minLength: {
              value: 8,
              message: 'Password must be at least 8 characters',
            },
          })}
        />
      </Box>

      {/* Confirm Password and Button Side-by-Side */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5 }}
          >
            Confirm New Password
          </Typography>
          <FormPassword
            size="small"
            placeholder="Confirm your new password"
            error={!!errors.confirm_password}
            helperText={errors.confirm_password?.message?.toString()}
            control={control}
            required
            sx={{
              width: 300,
              '& .MuiInputBase-root': {
                height: 40,
              },
              '& .MuiInputBase-input': {
                height: '40px',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
              },
            }}
            {...register('confirm_password', {
              required: 'Confirm New Password is required',
              validate: value => value === getValues('password') || 'Passwords do not match',
            })}
          />
        </Box>

        <LoadingButton
          loading={isSubmitting}
          variant="contained"
          type="submit"
          size="small"
          sx={{ height: 40 }}
        >
          Update Password
        </LoadingButton>
      </Box>

      {!!formError && <FormHelperText error>{formError.message}</FormHelperText>}
    </Stack>
  );
};

export default PasswordForm;
